import { Controller } from '@hotwired/stimulus';

interface ApiResponse {
    items: Array<{ id: number | string; name: string }>;
}

export default class SedeCascadeController extends Controller {
    static targets = ['sede', 'asociaciones', 'userGroups'];
    static values = {
        asociacionesUrl: String,
        userGroupsUrl: String,
    };

    declare readonly hasSedeTarget: boolean;
    declare readonly sedeTarget: HTMLSelectElement;
    declare readonly hasAsociacionesTarget: boolean;
    declare readonly asociacionesTarget: HTMLSelectElement;
    declare readonly hasUserGroupsTarget: boolean;
    declare readonly userGroupsTarget: HTMLSelectElement;
    declare readonly asociacionesUrlValue: string;
    declare readonly userGroupsUrlValue: string;

    async onSedeChange(): Promise<void> {
        if (!this.hasSedeTarget) {
            return;
        }

        const sedeId = this.sedeTarget.value;

        if (!sedeId) {
            this.clearSelect(this.asociacionesTarget);
            this.clearSelect(this.userGroupsTarget);
            return;
        }

        await Promise.all([
            this.refresh(this.asociacionesTarget, this.asociacionesUrlValue, sedeId),
            this.refresh(this.userGroupsTarget, this.userGroupsUrlValue, sedeId),
        ]);
    }

    private async refresh(select: HTMLSelectElement | undefined, urlTemplate: string, sedeId: string): Promise<void> {
        if (!select || !urlTemplate) {
            return;
        }

        const url = urlTemplate.replace('__SEDE__', encodeURIComponent(sedeId));

        try {
            const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            if (!response.ok) {
                console.warn(`sede-cascade: fetch ${url} failed (${response.status})`);
                return;
            }
            const data = await response.json() as ApiResponse;
            this.replaceOptions(select, data.items ?? []);
        } catch (err) {
            console.warn('sede-cascade: fetch error', err);
        }
    }

    private replaceOptions(select: HTMLSelectElement, items: Array<{ id: number | string; name: string }>): void {
        const ts = (select as any).tomselect;
        const options = items.map(item => ({ value: String(item.id), text: item.name }));

        if (ts) {
            ts.clear(true);
            ts.clearOptions();
            ts.addOptions(options);
            ts.refreshOptions(false);
            return;
        }

        select.innerHTML = '';
        options.forEach(opt => {
            const el = document.createElement('option');
            el.value = opt.value;
            el.textContent = opt.text;
            select.appendChild(el);
        });
    }

    private clearSelect(select: HTMLSelectElement | undefined): void {
        if (!select) {
            return;
        }
        const ts = (select as any).tomselect;
        if (ts) {
            ts.clear(true);
            ts.clearOptions();
            ts.refreshOptions(false);
            return;
        }
        select.innerHTML = '';
    }
}
